import { GetApiPayloadByUrl, GetApiResponseByUrl } from '@crossbeam/openapi';
import { Nullable, StrictExclude } from '@crossbeam/types';

import {
  HUBSPOT_DATA_SOURCE_TYPE,
  MD_DATA_SOURCE_TYPE,
  SALESFORCE_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';

import { Partner } from './partners';

export type IntegrationProfilesResponse =
  GetApiResponseByUrl<'/v0.1/integration-profiles'>;

export type IntegrationProfile = IntegrationProfilesResponse[0];

export type IntegrationProfileType = IntegrationProfile['integration_type'];

export type IntegrationProfilePushType = StrictExclude<
  IntegrationProfileType,
  'crossbeam_copilot'
>;

export type IntegrationProfileCrm =
  | typeof SALESFORCE_DATA_SOURCE_TYPE
  | typeof HUBSPOT_DATA_SOURCE_TYPE
  | typeof MD_DATA_SOURCE_TYPE
  | 'snowflake'
  | 'databricks'
  | undefined;

export type IntegrationProfilePopulationSetting =
  IntegrationProfile['population_settings'][0];

export type IntegrationProfileStandardPopulationSetting =
  IntegrationProfile['standard_population_settings'][0];

export type MinimalIntegrationProfile = {
  exclude_partnerships_created_since?: string | null;
  population_settings: IntegrationProfilePopulationSetting[];
  standard_population_settings: IntegrationProfileStandardPopulationSetting[];
};

export type IntegrationProfileStatus = 'warning' | 'error' | 'success';

export type ConnectionStatusType =
  | 'active'
  | 'paused'
  | 'setting_available'
  | 'action_needed'
  | 'error';

export const CONNECTION_STATUS_DISPLAY: Record<
  ConnectionStatusType,
  { text: string; type: IntegrationProfileStatus }
> = {
  active: { text: 'Active', type: 'success' },
  paused: { text: 'Paused', type: 'warning' },
  setting_available: { text: 'Setting available', type: 'warning' },
  action_needed: { text: 'Action needed', type: 'error' },
  error: { text: 'Error', type: 'error' },
};

export type EcosystemFieldsPush = 'ALL' | 'NONE';

export type EcosystemCrossbeamField = 'is_customer_of' | 'is_opportunity_for';
export type AccountObjectFieldErrorLookup = Record<
  EcosystemCrossbeamField,
  boolean
>;

export type EcosystemFieldMappings = Nullable<
  {
    crossbeam_field: EcosystemCrossbeamField;
    crm_field?: string;
  }[]
>;

export type PartnerAttributeCrossbeamField = 'tags';
export type PartnerAttributeFieldMappings = {
  crossbeam_field: PartnerAttributeCrossbeamField;
  crm_field?: string;
}[];

export type TrayPayload = GetApiPayloadByUrl<'/v0.1/tray-integrations', 'put'>;

export interface PartnerOption extends Partner {
  label: string;
  value: number;
}

export type SharedPartnerField = {
  nickname: Nullable<string>;
  column: string;
  source_field_id: number;
  organization_uuid: string;
};

export type PutPartnerFieldMappingsPayload = GetApiPayloadByUrl<
  '/v0.1/integration-profiles/{profile_id}/partner-field-mappings',
  'put'
>;

export type PartnerFieldMapping = GetApiResponseByUrl<
  '/v0.1/integration-profiles/{profile_id}/partner-field-mappings',
  'put'
>['items'][number];

export const API_FILTER_INTEGRATION_TYPE: IntegrationProfileType = 'api_filter';

export type ApiFilterCalculationStatusType =
  | 'pending'
  | 'running'
  | 'complete'
  | 'failed';

/** Hand-written until calculation_status reaches the OAS. */
export type ApiFilterProfile = IntegrationProfile & {
  calculation_status: {
    status: ApiFilterCalculationStatusType;
    last_completed_at: string | null;
  } | null;
};
